import type { RouteRecordRaw } from 'vue-router';

const privateRoutes: RouteRecordRaw = {
  path: '/dashboard',
  meta: {
    auth: true,
  },
  redirect: '/dashboard',
  component: () => import('@/layout/AppLayout.vue'),
  children: [
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/Dashboard.vue'),
    },
    {
      path: '/property-owners',
      name: 'propertyOwners',
      component: () => import('@/views/pages/propertyOwners/PropertyOwners.vue'),
    },
    {
      path: '/property-owners/form/:ulid?',
      name: 'PropertyOwnersForm',
      component: () => import('@/views/pages/propertyOwners/PropertyOwnerForm.vue'),
    },
    {
      path: '/roles',
      name: 'roles',
      component: () => import('@/views/pages/roles/RolesList.vue'),
    },
    {
      path: '/role/form/:role_id?',
      name: 'role form',
      component: () => import('@/views/pages/roles/RoleForm.vue'),
    },
    {
      path: '/data-sharing',
      name: 'data sharing',
      component: () => import('@/views/pages/data-sharing/DataSharingList.vue'),
    },
    {
      path: '/data-sharing/new',
      name: 'data sharing new',
      component: () => import('@/views/pages/data-sharing/NewRequest.vue'),
    },
    {
      path: '/data-sharing/partners',
      name: 'partner directory',
      component: () => import('@/views/pages/data-sharing/PartnerDirectory.vue'),
    },
    {
      path: '/platform/partners',
      name: 'platform partners',
      component: () => import('@/views/pages/platform/Partners.vue'),
    },
    {
      path: '/adverts',
      name: 'adverts',
      component: () => import('@/views/pages/adverts/Adverts.vue'),
    },
    {
      path: '/aggregators',
      name: 'aggregators',
      component: () => import('@/views/pages/aggregators/AggregatorsList.vue'),
    },
    {
      path: '/aggregator/form/:ulid?',
      name: 'aggregator edit',
      component: () => import('@//views/pages/aggregators/AggregatorForm.vue'),
      props: true,
    },
    {
      path: '/property-owner-form/:ulid',
      name: 'PropertyOwnerEdit',
      component: () => import('@/views/pages/propertyOwners/PropertyOwnerForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/properties',
      name: 'properties',
      component: () => import('@/views/pages/properties/Properties.vue'),
    },
    {
      path: '/property-form/:ulid?',
      name: 'PropertyEdit',
      component: () => import('@/views/pages/properties/PropertyForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/leads',
      name: 'leads',
      component: () => import('@/views/pages/leads/Leads.vue'),
    },
    {
      path: '/lead-form/:ulid?',
      name: 'LeadEdit',
      component: () => import('@/views/pages/leads/LeadForm.vue'),
      props: true,
    },
    {
      path: '/house-types',
      name: 'houseTypes',
      component: () => import('@/views/pages/houseTypes/HouseTypes.vue'),
    },
    {
      path: '/house-type-form/:ulid?',
      name: 'HouseTypeEdit',
      component: () => import('@/views/pages/houseTypes/HouseTypeForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/amenities',
      name: 'amenities',
      component: () => import('@/views/pages/amenities/Amenities.vue'),
    },

    {
      path: '/amenity-form/:ulid?',
      name: 'AmenityEdit',
      component: () => import('@/views/pages/amenities/AmenityForm.vue'),
      props: true,
    },
    {
      path: '/property-amenities',
      name: 'propertyAmenities',
      component: () => import('@/views/pages/amenities/PropertyAmenities.vue'),
    },
    {
      path: '/houses',
      name: 'Houses',
      component: () => import('@/views/pages/houses/Houses.vue'),
    },
    {
      path: '/houses-form/:ulid?',
      name: 'HousesEdit',
      component: () => import('@/views/pages/houses/HousesForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/house-form/:ulid?',
      name: 'HouseForm',
      component: () => import('@/views/pages/houses/HouseForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/:owner_ulid/house-form',
      name: 'OwnerHouseCreate',
      component: () => import('@/views/pages/houses/HousesForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/property/:property_id/house-form/:ulid?',
      name: 'PropertyHouseForm',
      component: () => import('@/views/pages/houses/HouseForm.vue'),
      props: true, // Enables passing `property_id` and `ulid` as props
    },
    {
      path: '/charges',
      name: 'charges',
      component: () => import('@/views/pages/charges/Charges.vue'),
      meta: {
        can: { module: 'charge', action: 'list' },
      },
    },
    {
      path: '/charge-form/:ulid?',
      name: 'ChargeEdit',
      component: () => import('@/views/pages/charges/ChargeForm.vue'),
      meta: {
        can: { module: 'charge', action: 'form' },
      },
    },
    {
      path: '/tenants',
      name: 'Tenants',
      component: () => import('@/views/pages/tenants/Tenants.vue'),
    },
    {
      path: '/tenant-form/:ulid?',
      name: 'TenantEdit',
      component: () => import('@/views/pages/tenants/TenantForm.vue'),
      props: true, // Enables passing `id` as a prop
    },
    {
      path: '/leases',
      name: 'leases',
      component: () => import('@/views/pages/leases/Leases.vue'),
    },
    {
      path: '/invoices',
      name: 'invoices',
      component: () => import('@/views/pages/invoices/Invoices.vue'),
      meta: {
        can: { module: 'invoice', action: 'list' },
      },
    },
    {
      path: '/invoice-form/:ulid?',
      name: 'InvoiceForm',
      component: () => import('@/views/pages/invoices/invoiceForm.vue'),
      meta: {
        can: { module: 'invoice', action: 'form' },
      },
    },
    {
      path: '/expenses',
      name: 'expenses',
      component: () => import('@/views/pages/expenses/Expenses.vue'),
      meta: {
        can: { module: 'expense', action: 'list' },
      },
    },
    {
      path: '/incomes',
      name: 'incomes',
      component: () => import('@/views/pages/incomes/Incomes.vue'),
      meta: {
        can: { module: 'income', action: 'list' },
      },
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => import('@/views/pages/payments/Payments.vue'),
    },
    {
      path: '/deletion-requests',
      name: 'deletion-requests',
      component: () => import('@/views/pages/deletion-requests/DeletionRequests.vue'),
    },
    {
      path: '/payment-channels',
      name: 'payment-channels',
      component: () => import('@/views/pages/payment_channels/PaymentChannels.vue'),
    },
    {
      path: '/payment-channel-form/:ulid?',
      name: 'payment-channel-form',
      component: () => import('@/views/pages/payment_channels/PaymentChannelForm.vue'),
    },
    {
      path: '/send-message/:ulid?',
      name: 'send-message',
      component: () => import('@/views/pages/messaging/messageForm.vue'),
    },
    {
      path: '/outbox',
      name: 'Outbox',
      component: () => import('@/views/pages/messaging/outbox.vue'),
    },
    {
      path: '/sent',
      name: 'Sent',
      component: () => import('@/views/pages/messaging/sent.vue'),
    },
    {
      path: '/statements/property/:ulid?',
      name: 'PropertyStatement',
      component: () => import('@/views/pages/reports/PropertyStatement.vue'),
    },
    {
      path: '/statements/lease/:tenant_id?/:lease_id?',
      name: 'LeaseStatement',
      component: () => import('@/views/pages/reports/LeaseStatement.vue'),
    },
    {
      path: '/statements/aging-balances',
      name: 'AgingBalanceReport',
      component: () => import('@/views/pages/reports/AgingBalanceReport.vue'),
    },
    {
      path: '/reports/expiring-leases',
      name: 'expiring-leases',
      component: () => import('@/views/pages/reports/ExpiringLeases.vue'),
      meta: { can: { module: 'lease', action: 'list' } },
    },
    {
      path: '/reports/property-summary',
      name: 'PropertySummary',
      component: () => import('@/views/pages/reports/PropertySummary.vue'),
    },
    {
      path: '/reports/property-payments',
      name: 'PropertyPayments',
      component: () => import('@/views/pages/reports/PropertyPayments.vue'),
    },
    {
      path: '/reports/detailed-invoices',
      name: 'DetailedInvoices',
      component: () => import('@/views/pages/reports/DetailedInvoices.vue'),
    },
    {
      path: '/reports/maintenance',
      name: 'maintenance-report',
      component: () => import('@/views/pages/reports/MaintenanceReport.vue'),
    },
    {
      path: '/reports/tenant-activity',
      name: 'TenantActivityReport',
      component: () => import('@/views/pages/reports/TenantActivityReport.vue'),
    },
    {
      path: '/reports/meter-readings',
      name: 'MeterReadingReport',
      component: () => import('@/views/pages/reports/MeterReadingReport.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/pages/settings.vue'),
    },
    {
      path: '/trail-logs',
      name: 'trail-logs',
      component: () => import('@/views/pages/audit/Audit.vue'),
    },
    {
      path: '/templates',
      name: 'templates',
      component: () => import('@/views/pages/templates/Templates.vue'),
    },
    {
      path: '/template-form/:ulid?',
      name: 'template-form',
      component: () => import('@/views/pages/templates/TemplateForm.vue'),
    },
    {
      path: '/users',
      name: 'Users',
      component: () => import('@/views/pages/users/Users.vue'),
    },
    {
      path: '/user-form/:ulid?',
      name: 'UserEdit',
      component: () => import('@/views/pages/users/UserForm.vue'),
    },
    {
      path: '/change-password',
      name: 'changePassword',
      component: () => import('@/views/pages/users/ChangePassword.vue'),
    },
    {
      path: '/work-orders',
      name: 'WorkOrders',
      component: () => import('@/views/pages/workOrders/WorkOrders.vue'),
    },
    {
      path: '/work-orders/:id/items',
      name: 'work-order-items',
      component: () => import('@/views/pages/workOrders/WorkOrderItems.vue'),
    },
    {
      path: '/workorder-form/:id?',
      name: 'WorkOrderForm',
      component: () => import('@/views/pages/workOrders/WorkOrdersForm.vue'),
    },
    {
      path: '/tickets',
      name: 'Tickets',
      component: () => import('@/views/pages/tickets/Tickets.vue'),
    },
    {
      path: '/ticket-form/:ulid?',
      name: 'TicketEdit',
      component: () => import('@/views/pages/tickets/TicketForm.vue'),
    },
    {
      path: '/service-providers',
      name: 'ServiceProviders',
      component: () => import('@/views/pages/serviceProvider/ServiceProvider.vue'),
      meta: {
        can: { module: 'service_provider', action: 'list' },
      },
    },
    {
      path: '/service-provider-form/:id?',
      name: 'ServiceProviderForm',
      component: () => import('@/views/pages/serviceProvider/ServiceProviderForm.vue'),
      props: true,
      meta: {
        can: { module: 'service_provider', action: 'form' },
      },
    },
    {
      path: '/sms-credits/top-up',
      name: 'SmsCreditsTopUp',
      component: () => import('@/views/pages/smsCredits/TopUp.vue'),
    },
    {
      path: '/sms-credits/history',
      name: 'SmsCreditsHistory',
      component: () => import('@/views/pages/smsCredits/History.vue'),
    },
    {
      path: '/work-order-categories',
      name: 'WorkOrderCategories',
      component: () => import('@/views/pages/workOrderCategories/WorkOrderCategories.vue'),
    },
    {
      path: '/work-order-category-form/:ulid?',
      name: 'WorkOrderCategoryForm',
      component: () => import('@/views/pages/workOrderCategories/WorkOrderCategoryForm.vue'),
    },
    {
      path: '/visitors',
      name: 'Visitors',
      component: () => import('@/views/pages/visitors/VisitorsList.vue'),
      meta: { can: { module: 'visitor', action: 'list' } },
    },
    {
      path: '/visits',
      name: 'Visits',
      component: () => import('@/views/pages/visitors/VisitsList.vue'),
      meta: { can: { module: 'visit', action: 'list' } },
    },
    {
      path: '/reports/visit-summary',
      name: 'VisitSummary',
      component: () => import('@/views/pages/visitors/VisitReport.vue'),
      meta: { can: { module: 'visit', action: 'download' } },
    },
    // Old address of the visit summary, kept for bookmarks.
    { path: '/visits/report', redirect: '/reports/visit-summary' },
  ],
};

export default privateRoutes;
